import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Plus as PlusIcon } from '@untitledui/icons/Plus';
import { Trash01 } from '@untitledui/icons/Trash01';
import { useState } from 'react';
import { z } from 'zod';

import { AlertFloating } from '@/components/application/alerts/alerts';
import { Avatar } from '@/components/base/avatar/avatar';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Divider } from '@/components/base/divider/divider';
import { Input } from '@/components/base/input/input';
import { ConfirmationModal } from '@/components/base/modals/confirmation-modal';
import { ControlledModal } from '@/components/base/modals/modal';
import { notify } from '@/components/base/notifications/notifications';
import { MultiSelectDropdown } from '@/components/base/select/multi-select-dropdown';
import { Select } from '@/components/base/select/select';
import { queryKeys } from '@/hooks/api/query-keys';
import { formatDate } from '@/utils/date';

import type {
  InvitableTeam,
  OrganizationInvitations,
  PendingInvitation,
} from '../-utils/organization-settings-server';

import { organizationInvitationsQueryOptions } from '../-utils/organization-settings-query';
import {
  deleteInvitation,
  inviteToOrganization,
  removeOrganizationMember,
} from '../-utils/organization-settings-server';
import { addTeamMember } from '../-utils/team-settings-server';
import { SettingsSection } from '../../settings/-components/settings-section';
import { SETTINGS_HELP } from '../../settings/-utils/help-articles';

export const Route = createFileRoute(
  '/researchers/_authenticated/organization-settings/organization/invitations',
)({
  component: InvitationsPage,
  head: () => ({ meta: [{ title: t`Manage invitations` }] }),
});

type Unassigned = OrganizationInvitations['unassigned'][number];

const fullName = (m: { firstName: string; lastName: string }) =>
  `${m.firstName} ${m.lastName}`.trim();

function InvitationsPage() {
  const { activeDashboard } = Route.useRouteContext();
  const query = useQuery(
    organizationInvitationsQueryOptions(activeDashboard.organization ?? ''),
  );
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.workspaceSettings.all(),
    });

  if (query.isError) {
    return (
      <SettingsSection title={t`Manage invitations`}>
        <AlertFloating
          color="error"
          title={t`We couldn't load invitations`}
          description={t`Refresh the page to try again.`}
          confirmLabel=""
        />
      </SettingsSection>
    );
  }
  if (query.isPending) {
    return (
      <SettingsSection title={t`Manage invitations`}>
        <div className="h-48 animate-pulse rounded-lg bg-secondary" />
      </SettingsSection>
    );
  }

  const data = query.data;
  const unassigned = (
    <SettingsSection
      title={t`Unassigned users`}
      description={t`People in your organization who aren't on any team yet.`}
      helpUrl={data.hasEnabledSsoProvider ? SETTINGS_HELP.teams : undefined}
    >
      {data.unassigned.length === 0 ? (
        <p className="text-sm text-tertiary">
          <Trans>Everyone in the organization is on a team.</Trans>
        </p>
      ) : (
        <UnassignedUsers
          users={data.unassigned}
          teams={data.teams}
          canRemove={data.canRemoveFromOrg}
          onChanged={() => void refresh()}
        />
      )}
    </SettingsSection>
  );

  // With an SSO provider enabled, people join by signing in, so legacy showed
  // only the unassigned users (and named the page after them).
  if (data.hasEnabledSsoProvider) return unassigned;

  return (
    <div className="flex flex-col gap-6">
      <SettingsSection
        title={t`Invite people`}
        description={t`Invite colleagues to your organization and the teams they'll work on.`}
        helpUrl={SETTINGS_HELP.teams}
      >
        {data.teams.length === 0 ? (
          <p className="text-sm text-tertiary">
            <Trans>
              You can only invite people to teams you&apos;re on. Join a team
              first, or ask an organization admin to invite them.
            </Trans>
          </p>
        ) : (
          <InviteForm
            teams={data.teams}
            canInviteAdmins={data.canInviteAdmins}
            onSent={() => void refresh()}
          />
        )}
      </SettingsSection>

      <SettingsSection title={t`Pending invitations`}>
        <PendingInvitations
          invitations={data.invitations}
          onChanged={() => void refresh()}
        />
      </SettingsSection>

      {data.unassigned.length > 0 && unassigned}
    </div>
  );
}

type Row = {
  key: number;
  email: string;
  role: 'admin' | 'member';
  teamIds: string[];
};

const emailSchema = z.email();

function InviteForm({
  teams,
  canInviteAdmins,
  onSent,
}: {
  teams: InvitableTeam[];
  canInviteAdmins: boolean;
  onSent: () => void;
}) {
  // A single team is preselected, as legacy did.
  const blankRow = (key: number): Row => ({
    key,
    email: '',
    role: 'member',
    teamIds: teams.length === 1 && teams[0] ? [teams[0].id] : [],
  });
  const [rows, setRows] = useState<Row[]>([blankRow(0)]);
  const [submitted, setSubmitted] = useState(false);

  const rowError = (row: Row): string | undefined => {
    if (!emailSchema.safeParse(row.email.trim()).success) {
      return t`Enter a valid email address`;
    }
    if (row.teamIds.length === 0) return t`Choose at least one team`;
    return undefined;
  };
  const isValid = rows.every((row) => !rowError(row));

  const send = useMutation({
    mutationFn: async () => {
      const result = await inviteToOrganization({
        data: {
          invitations: rows.map((row) => ({
            email: row.email.trim(),
            role: row.role,
            teamIds: row.teamIds,
          })),
        },
      });
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
      return result;
    },
    onSuccess: (result) => {
      if (result.sent > 0) {
        notify({
          title:
            result.sent === 1
              ? t`Invitation sent`
              : t`${result.sent} invitations sent`,
          color: 'success',
        });
      }
      for (const failure of result.failed) {
        notify({
          title: t`Couldn't invite ${failure.email}`,
          description: failure.message,
          color: 'error',
        });
      }
      // Keep the rows that failed so they can be fixed and sent again.
      const failedEmails = new Set(result.failed.map((f) => f.email));
      const remaining = rows.filter((row) =>
        failedEmails.has(row.email.trim().toLowerCase()),
      );
      setRows(remaining.length > 0 ? remaining : [blankRow(Date.now())]);
      setSubmitted(false);
      onSent();
    },
    onError: (error) =>
      notify({
        title: t`Couldn't send invitations`,
        description: error.message,
        color: 'error',
      }),
  });

  const update = (key: number, patch: Partial<Row>) =>
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );

  const teamItems = teams.map((team) => ({ id: team.id, label: team.name }));
  const roleItems = [
    { id: 'member', label: t`Member` },
    ...(canInviteAdmins ? [{ id: 'admin', label: t`Admin` }] : []),
  ];

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        setSubmitted(true);
        if (isValid) send.mutate();
      }}
    >
      {rows.map((row, index) => {
        const error = submitted ? rowError(row) : undefined;
        return (
          <div
            key={row.key}
            className="grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_9rem_1fr_auto]"
          >
            <Input
              label={index === 0 ? t`Email` : undefined}
              aria-label={t`Email ${index + 1}`}
              placeholder={t`name@company.com`}
              value={row.email}
              onChange={(email) => update(row.key, { email })}
              isInvalid={!!error}
              hint={error}
              size="xs"
            />
            <Select
              label={index === 0 ? t`Role` : undefined}
              aria-label={t`Role ${index + 1}`}
              size="xs"
              value={row.role}
              onSelectionChange={(role) => {
                if (role === 'admin' || role === 'member') {
                  update(row.key, { role });
                }
              }}
              items={roleItems}
            >
              {(item) => <Select.Item {...item} />}
            </Select>
            {/* Every team in the organization is listed, filtered locally,
                which is what ONCALL-2217 reported broken in legacy. */}
            <MultiSelectDropdown
              label={index === 0 ? t`Teams` : undefined}
              placeholder={t`Choose teams`}
              size="xs"
              items={teamItems}
              selectedKeys={row.teamIds}
              onSelectionChange={(keys) =>
                update(row.key, { teamIds: [...keys] })
              }
              maxDisplay={2}
            />
            <div className={index === 0 ? 'md:pt-6' : undefined}>
              {rows.length > 1 && (
                <Button
                  color="tertiary"
                  size="sm"
                  iconLeading={Trash01}
                  aria-label={t`Remove invite ${index + 1}`}
                  onClick={() =>
                    setRows((current) =>
                      current.filter((r) => r.key !== row.key),
                    )
                  }
                />
              )}
            </div>
          </div>
        );
      })}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          color="link-color"
          size="sm"
          iconLeading={PlusIcon}
          onClick={() =>
            setRows((current) => [...current, blankRow(Date.now())])
          }
        >
          <Trans>Invite another person</Trans>
        </Button>
        <Button
          type="submit"
          color="primary"
          size="md"
          isLoading={send.isPending}
        >
          {rows.length === 1 ? (
            <Trans>Send invitation</Trans>
          ) : (
            <Trans>Send invitations</Trans>
          )}
        </Button>
      </div>
    </form>
  );
}

function PendingInvitations({
  invitations,
  onChanged,
}: {
  invitations: PendingInvitation[];
  onChanged: () => void;
}) {
  const [deleting, setDeleting] = useState<PendingInvitation | null>(null);
  const remove = useMutation({
    mutationFn: async (key: string) => {
      const result = await deleteInvitation({ data: { key } });
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
    },
    onSuccess: () => {
      setDeleting(null);
      notify({ title: t`Invitation deleted`, color: 'success' });
      onChanged();
    },
    onError: (error) =>
      notify({
        title: t`Couldn't delete the invitation`,
        description: error.message,
        color: 'error',
      }),
  });

  if (invitations.length === 0) {
    return (
      <p className="text-sm text-tertiary">
        <Trans>There are no pending invitations.</Trans>
      </p>
    );
  }
  return (
    <>
      <ul className="divide-y divide-secondary">
        {invitations.map((invitation) => (
          <li
            key={invitation.key ?? invitation.email}
            className="flex items-center gap-3 py-3"
          >
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex items-center gap-2">
                <p className="truncate text-sm font-medium text-primary">
                  {invitation.email}
                </p>
                <Badge type="pill-color" color="gray" size="sm">
                  {invitation.role === 'admin' ? t`Admin` : t`Member`}
                </Badge>
              </div>
              {invitation.createdAt && (
                <p className="text-sm text-tertiary">
                  <Trans>Sent {formatDate(invitation.createdAt)}</Trans>
                </p>
              )}
            </div>
            {invitation.key && (
              <Button
                color="tertiary"
                size="sm"
                iconLeading={Trash01}
                aria-label={t`Delete invitation for ${invitation.email}`}
                onClick={() => setDeleting(invitation)}
              />
            )}
          </li>
        ))}
      </ul>
      <ConfirmationModal
        isOpen={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t`Delete the invitation for ${deleting?.email ?? ''}?`}
        description={t`The link in their email will stop working.`}
        confirmLabel={t`Delete invitation`}
        isDestructive
        isLoading={remove.isPending}
        onConfirm={() => deleting?.key && remove.mutate(deleting.key)}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

function UnassignedUsers({
  users,
  teams,
  canRemove,
  onChanged,
}: {
  users: Unassigned[];
  teams: InvitableTeam[];
  canRemove: boolean;
  onChanged: () => void;
}) {
  const [assigning, setAssigning] = useState<Unassigned | null>(null);
  const [removing, setRemoving] = useState<Unassigned | null>(null);
  const remove = useMutation({
    mutationFn: async (userId: string) => {
      const result = await removeOrganizationMember({ data: { userId } });
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
    },
    onSuccess: () => {
      setRemoving(null);
      notify({ title: t`Removed from the organization`, color: 'success' });
      onChanged();
    },
    onError: (error) =>
      notify({
        title: t`Couldn't remove them`,
        description: error.message,
        color: 'error',
      }),
  });

  return (
    <>
      <ul className="divide-y divide-secondary">
        {users.map((user) => (
          <li key={user.id} className="flex items-center gap-3 py-3">
            <Avatar size="md" src={user.picture} alt={fullName(user)} />
            <div className="flex min-w-0 flex-1 flex-col">
              <p className="truncate text-sm font-semibold text-primary">
                {fullName(user)}
              </p>
              <p className="truncate text-sm text-tertiary">{user.email}</p>
            </div>
            {teams.length > 0 && (
              <Button
                color="secondary"
                size="sm"
                onClick={() => setAssigning(user)}
              >
                <Trans>Add to teams</Trans>
              </Button>
            )}
            {canRemove && (
              <Button
                color="tertiary"
                size="sm"
                iconLeading={Trash01}
                aria-label={t`Remove ${fullName(user)} from the organization`}
                onClick={() => setRemoving(user)}
              />
            )}
          </li>
        ))}
      </ul>

      {assigning && (
        <AssignTeamsModal
          user={assigning}
          teams={teams}
          onClose={() => {
            setAssigning(null);
            onChanged();
          }}
        />
      )}

      <ConfirmationModal
        isOpen={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={t`Remove ${removing ? fullName(removing) : ''} from the organization?`}
        description={t`They lose access to the organization.`}
        confirmLabel={t`Remove`}
        isDestructive
        isLoading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}

function AssignTeamsModal({
  user,
  teams,
  onClose,
}: {
  user: Unassigned;
  teams: InvitableTeam[];
  onClose: () => void;
}) {
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const add = useMutation({
    mutationFn: async (teamId: string) => {
      const result = await addTeamMember({ data: { teamId, userId: user.id } });
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
      return teamId;
    },
    onSuccess: (teamId) => setAdded((current) => new Set(current).add(teamId)),
    onError: (error) =>
      notify({
        title: t`Couldn't add them to the team`,
        description: error.message,
        color: 'error',
      }),
  });
  const needle = search.trim().toLowerCase();
  const visible = teams.filter(
    (team) => !needle || team.name.toLowerCase().includes(needle),
  );

  return (
    <ControlledModal
      isOpen
      onOpenChange={(open) => !open && onClose()}
      centered
      title={t`Add ${fullName(user)} to teams`}
      className="mx-auto w-full max-w-md rounded-xl bg-primary p-6 shadow-xl"
    >
      <div className="flex w-full flex-col gap-4">
        <h2 className="text-lg font-semibold text-primary">
          <Trans>Add {fullName(user)} to teams</Trans>
        </h2>
        <Input
          aria-label={t`Find teams`}
          placeholder={t`Find teams`}
          value={search}
          onChange={setSearch}
          size="xs"
        />
        <ul className="max-h-80 divide-y divide-secondary overflow-y-auto">
          {visible.map((team) => (
            <li key={team.id} className="flex items-center gap-3 py-2">
              <p className="min-w-0 flex-1 truncate text-sm text-primary">
                {team.name}
              </p>
              {added.has(team.id) ? (
                <Badge type="pill-color" color="success" size="sm">
                  <Trans>Added</Trans>
                </Badge>
              ) : (
                <Button
                  color="secondary"
                  size="xs"
                  isDisabled={add.isPending}
                  isLoading={add.isPending && add.variables === team.id}
                  onClick={() => add.mutate(team.id)}
                >
                  <Trans>Add</Trans>
                </Button>
              )}
            </li>
          ))}
        </ul>
        <Divider />
        <div className="flex justify-end">
          <Button color="secondary" size="md" onClick={onClose}>
            <Trans>Done</Trans>
          </Button>
        </div>
      </div>
    </ControlledModal>
  );
}
